import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import { useMountedRef } from "../hooks/use-mounted-ref";
import { isPromise } from "../../../common";

// TODO: allow for children to be passed in for custom footer content
const ModalFooter = (props) => {
  const {
    cancelButtonLabel,
    className,
    isForm = true,
    okButtonLabel,
    okButtonDisabled = false,
    onOk,
    onCancel,
    loading,
    formId,
    cancelButtonClassName,
    okButtonClassName,
    tertiaryButtonLabel,
    tertiaryButtonClassName,
    tertiaryButtonDisabled = false,
    onTertiary,
  } = props;
  return (
    <div className={`${className || ""} localist-modal-footer`}>
      {!!tertiaryButtonLabel && (
        <button
          type="button"
          className={`localist-modal-tertiary-btn ${tertiaryButtonClassName || ""}`}
          disabled={tertiaryButtonDisabled}
          onClick={onTertiary}
        >
          {tertiaryButtonLabel}
        </button>
      )}
      <button type="button" className={cancelButtonClassName} onClick={onCancel}>
        {cancelButtonLabel}
      </button>
      <button
        className={okButtonClassName}
        type={isForm ? "submit" : "button"}
        form={formId}
        disabled={loading || okButtonDisabled}
        onClick={onOk}
      >
        {!!loading && (
          <i className="fa fa-spinner fa-spin" aria-hidden="true" />
        )}
        {okButtonLabel}
      </button>
    </div>
  );
}


// TODO: make this the "confirm modal" component which calls a more abstracted modal component
export const Modal = (props) => {
  const {
    cancelButtonLabel,
    children,
    className,
    isOpen,
    loading: externalLoading = false,
    okButtonLabel,
    okButtonDisabled,
    onOk,
    onRequestClose,
    title,
    formId,
    cancelButtonClassName,
    okButtonClassName,
    footerClassName,
    hideCloseButton = false,
    tertiaryButtonLabel,
    tertiaryButtonClassName,
    tertiaryButtonDisabled,
    onTertiary,
  } = props;

  const mounted = useMountedRef();

  const [internalLoading, setInternalLoading] = useState(false);
  // Combine external and internal loading states
  const loading = externalLoading || internalLoading;
  const dialogRef = useRef(null);
  const instanceIdRef = useRef(null);
  if (!instanceIdRef.current) {
    // TODO: use useId hook when we upgrade to React 18
    instanceIdRef.current = `localist_modal_${Math.floor(Math.random() * 1000000000)}`;
  }
  const modalId = instanceIdRef.current;
  const titleId = `${modalId}_title`;
  const titleRef = useRef(null);

  useEffect(() => {
    const dialogEl = dialogRef.current;
    if (!dialogEl) return;
    if (isOpen && !dialogEl.open) {
      dialogEl.showModal();
      // Move focus to the title for accessibility
      if (titleRef.current) {
        // Defer to next frame to ensure it's rendered
        requestAnimationFrame(() => titleRef.current && titleRef.current.focus());
      }
    } else if (!isOpen && dialogEl.open) {
      dialogEl.close();
    }

    return () => {
      if (dialogEl && dialogEl.open) {
        dialogEl.close();
      }
    }
  }, [isOpen]);

  // Handle the native cancel event (fired on Escape key) to sync with React state
  useEffect(() => {
    const dialogEl = dialogRef.current;
    if (!dialogEl) return;

    const handleCancel = (e) => {
      e.preventDefault(); // prevent native auto-close
      onRequestClose();   // call React's close handler
    };

    dialogEl.addEventListener('cancel', handleCancel);
    return () => {
      dialogEl.removeEventListener('cancel', handleCancel);
    };
  }, [onRequestClose]);

  const handleRequestClose = useCallback(() => {
    onRequestClose();
  }, [onRequestClose]);

  const handleOk = useCallback(async () => {
    const result = onOk();
    if (!isPromise(result)) {
      return;
    }

    if (mounted.current) {
      setInternalLoading(true);
      await result;
      setInternalLoading(false);
    }
  }, [mounted, onOk]);

  // TODO: make clicking outside the dialog close it or not depending on a prop
  const handleDialogClick = useCallback((e) => {
    // Only handle actual mouse clicks, not synthetic clicks from keyboard events.
    // e.detail is 0 for synthetic/keyboard-triggered clicks, 1+ for actual mouse clicks.
    // This prevents keyboard navigation (like arrow keys on radio buttons) from
    // accidentally closing the modal due to missing/invalid clientX/clientY coordinates.
    if (e.detail === 0) return;

    const dialogEl = dialogRef.current;
    if (!dialogEl) return;
    const rect = dialogEl.getBoundingClientRect();
    const isInDialog = (
      rect.top <= e.clientY && e.clientY <= rect.top + rect.height &&
      rect.left <= e.clientX && e.clientX <= rect.left + rect.width
    );
    if (!isInDialog) {
      handleRequestClose();
    }
  }, [handleRequestClose]);


  const modalBody = (
    <dialog
      id={modalId}
      ref={dialogRef}
      className={`${className || ''} localist-modal`}
      aria-labelledby={titleId}
      onClick={handleDialogClick}
    >
      {!hideCloseButton && (
        <button
          type="button"
          className="localist-modal-close"
          aria-label="Close"
          onClick={handleRequestClose}
        >
          <i className="fa fa-times" aria-hidden="true" />
        </button>
      )}
      <h2 id={titleId} tabIndex={-1} ref={titleRef}>{title}</h2>
      <div>
        {!!isOpen && children}
      </div>
      <ModalFooter
        cancelButtonLabel={cancelButtonLabel}
        className={footerClassName}
        loading={loading}
        okButtonLabel={okButtonLabel}
        okButtonDisabled={okButtonDisabled}
        onOk={handleOk}
        onCancel={handleRequestClose}
        formId={formId}
        cancelButtonClassName={cancelButtonClassName}
        okButtonClassName={okButtonClassName}
        tertiaryButtonLabel={tertiaryButtonLabel}
        tertiaryButtonClassName={tertiaryButtonClassName}
        tertiaryButtonDisabled={tertiaryButtonDisabled}
        onTertiary={onTertiary}
        isForm={!!formId}
      />
    </dialog>
  )

  return createPortal(modalBody, document.body);
}

Modal.propTypes = {
  cancelButtonLabel: PropTypes.string.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  isOpen: PropTypes.bool,
  loading: PropTypes.bool,
  okButtonLabel: PropTypes.string.isRequired,
  okButtonDisabled: PropTypes.bool,
  onOk: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  title: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  formId: PropTypes.string,
  cancelButtonClassName: PropTypes.string,
  okButtonClassName: PropTypes.string,
  footerClassName: PropTypes.string,
  // When true, the header "X" close button is not rendered. Defaults to shown.
  hideCloseButton: PropTypes.bool,
  // Optional third footer button (rendered left of cancel/ok), e.g. "Regenerate".
  tertiaryButtonLabel: PropTypes.string,
  tertiaryButtonClassName: PropTypes.string,
  tertiaryButtonDisabled: PropTypes.bool,
  onTertiary: PropTypes.func,
};

ModalFooter.propTypes = {
  cancelButtonLabel: PropTypes.string.isRequired,
  className: PropTypes.string,
  isForm: PropTypes.bool,
  okButtonLabel: PropTypes.string.isRequired,
  okButtonDisabled: PropTypes.bool,
  onOk: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  formId: PropTypes.string,
  cancelButtonClassName: PropTypes.string,
  okButtonClassName: PropTypes.string,
  tertiaryButtonLabel: PropTypes.string,
  tertiaryButtonClassName: PropTypes.string,
  tertiaryButtonDisabled: PropTypes.bool,
  onTertiary: PropTypes.func,
};
